import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Search, Store, MapPin, Star, ChevronDown, Package, 
  Truck, Clock, Phone, Filter, ShoppingCart, Globe, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supermarket, turkishMarkets } from '@/lib/api';
import { toast } from 'sonner';
import { worldCountries, getCountryKey, getCityKey, getDistrictKey } from '@/lib/countries';
import { PageSeo } from '@/components/seo/PageSeo';

const SupermarketsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  // Seller stores (registered sellers)
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(true);
  
  // Turkish Markets (worldwide directory)
  const [markets, setMarkets] = useState([]);
  const [loadingMarkets, setLoadingMarkets] = useState(true);
  const [marketCountries, setMarketCountries] = useState([]);
  const [selectedMarketCountry, setSelectedMarketCountry] = useState('');
  
  const [searchQuery, setSearchQuery] = useState('');
  
  // Location filters for seller stores
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  
  // Available options based on selection
  const [availableCities, setAvailableCities] = useState([]);
  const [availableDistricts, setAvailableDistricts] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      const cities = Object.keys(worldCountries[selectedCountry] || {});
      setAvailableCities(cities);
      setSelectedCity('');
      setSelectedDistrict('');
      setAvailableDistricts([]);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedCountry && selectedCity) {
      const districts = worldCountries[selectedCountry]?.[selectedCity] || [];
      setAvailableDistricts(districts);
      setSelectedDistrict('');
    }
  }, [selectedCity]);

  useEffect(() => {
    fetchStores();
  }, [selectedCountry, selectedCity, selectedDistrict]);

  useEffect(() => {
    fetchMarkets();
  }, [selectedMarketCountry, searchQuery]);

  const fetchInitialData = async () => {
    try {
      setLoadingStores(true);
      setLoadingMarkets(true);
      
      const [storesRes, marketsRes, countriesRes] = await Promise.all([
        supermarket.getStores({ limit: 50 }),
        turkishMarkets.list({ limit: 50 }),
        turkishMarkets.getCountries()
      ]);
      
      setStores(storesRes.data?.stores || []);
      setMarkets(marketsRes.data?.markets || []);
      setMarketCountries(countriesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoadingStores(false);
      setLoadingMarkets(false);
    }
  };

  const fetchStores = async () => {
    try {
      const params = { limit: 50 };
      if (selectedCountry) params.country = selectedCountry;
      if (selectedCity) params.city = selectedCity;
      if (selectedDistrict) params.district = selectedDistrict;
      if (searchQuery) params.search = searchQuery;
      
      const res = await supermarket.getStores(params);
      setStores(res.data?.stores || []);
    } catch (error) {
      console.error('Failed to fetch stores:', error);
    }
  };

  const fetchMarkets = async () => {
    try {
      setLoadingMarkets(true);
      const params = { limit: 50 };
      if (selectedMarketCountry && selectedMarketCountry !== 'all') {
        params.country = selectedMarketCountry;
      }
      if (searchQuery) params.q = searchQuery;
      
      const res = await turkishMarkets.list(params);
      setMarkets(res.data?.markets || []);
    } catch (error) {
      console.error('Failed to fetch markets:', error);
    } finally {
      setLoadingMarkets(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchStores();
    fetchMarkets();
  };

  const heroImage = "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=1200";

  // Get translated country name
  const getCountryName = (country) => {
    const key = getCountryKey(country);
    const translated = t(`location.${key}`, { defaultValue: '' });
    return translated || country;
  };

  // Get translated city name
  const getCityName = (city) => {
    const key = getCityKey(city);
    const translated = t(`location.cities.${key}`, { defaultValue: '' });
    return translated || city;
  };

  // Get translated district name
  const getDistrictName = (district) => {
    const key = getDistrictKey(district);
    const translated = t(`location.districts.${key}`, { defaultValue: '' });
    return translated || district;
  };

  return (
    <div data-testid="supermarkets-page" className="min-h-screen bg-background">
      <PageSeo
        title={t('supermarket.title')}
        description={t('supermarket.heroSubtitle')}
        segments={['supermarkets']}
      />
      {/* Hero Section */}
      <section className="relative h-[45vh] min-h-[350px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/95 via-emerald-800/85 to-emerald-700/70 z-10" />
        <img
          src={heroImage}
          alt={t('supermarket.title')}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 z-20 flex items-center">
          <div className="container mx-auto px-4 md:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-4">
                <Store className="w-10 h-10 text-white" />
                <Badge className="bg-emerald-500 hover:bg-emerald-600 text-sm">
                  {t('supermarket.worldwideBadge')}
                </Badge>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white font-bold leading-tight mb-4">
                {t('supermarket.title')}
              </h1>
              <p className="text-lg md:text-xl text-white/90 mb-6">
                {t('supermarket.heroSubtitle')}
              </p>
              
              {/* Search Form */}
              <form onSubmit={handleSearch} className="flex gap-2 max-w-xl">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="search"
                    placeholder={t('supermarket.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-14 rounded-full bg-white text-black text-lg"
                    data-testid="supermarket-search"
                  />
                </div>
                <Button type="submit" size="lg" className="h-14 px-8 rounded-full bg-white text-emerald-700 hover:bg-gray-100">
                  {t('common.search')}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Turkish Markets Directory Section */}
      <section className="py-8 md:py-12 bg-emerald-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-serif font-bold flex items-center gap-2">
                <Globe className="w-6 h-6 text-emerald-600" />
                {t('supermarket.nearYou')}
              </h2>
              <p className="text-muted-foreground mt-1">
                {t('supermarket.marketsDirectoryDesc')}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Select value={selectedMarketCountry} onValueChange={setSelectedMarketCountry}>
                <SelectTrigger className="w-[200px] h-11 rounded-full" data-testid="market-country-filter">
                  <Globe className="w-4 h-4 mr-2" />
                  <SelectValue placeholder={t('location.selectCountry')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('location.allCountries')}</SelectItem>
                  {marketCountries.map((c) => (
                    <SelectItem key={c.country} value={c.country}>
                      {c.country} ({c.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Badge variant="outline" className="h-11 px-4 text-sm">
                {markets.length} {t('supermarket.marketsLabel')}
              </Badge>
            </div>
          </div>

          {loadingMarkets ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-16 w-16 rounded-xl mb-4" />
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-4" />
                    <Skeleton className="h-10 w-full rounded-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : markets.length === 0 ? (
            <Card className="p-12">
              <div className="text-center">
                <Globe className="w-20 h-20 mx-auto text-emerald-200 mb-6" />
                <h3 className="text-2xl font-semibold mb-3">
                  {t('supermarket.noMarketsFound')}
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  {t('supermarket.noMarketsDesc')}
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedMarketCountry('')}
                >
                  {t('supermarket.viewAllStores')}
                </Button>
              </div>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {markets.map((market) => (
                <Card 
                  key={market.id} 
                  className="group hover:shadow-xl transition-all duration-300 overflow-hidden"
                  data-testid={`market-${market.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                        <Store className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg group-hover:text-red-600 transition-colors truncate">
                          {market.name}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="w-4 h-4 shrink-0" />
                          <span className="truncate">
                            {[market.city, market.country].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {market.description && (
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {market.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                      {market.categories?.slice(0, 2).map((cat, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {cat}
                        </Badge>
                      ))}
                      {market.rating > 0 && (
                        <Badge variant="outline" className="text-xs">
                          <Star className="w-3 h-3 mr-1 fill-yellow-400 text-yellow-400" />
                          {market.rating.toFixed(1)}
                        </Badge>
                      )}
                    </div>

                    {market.address && (
                      <p className="text-xs text-muted-foreground mb-3 line-clamp-1">
                        {market.address}
                      </p>
                    )}

                    <div className="flex gap-2">
                      {market.website && (
                        <a 
                          href={market.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button variant="outline" className="w-full rounded-full" size="sm">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            {t('supermarket.website')}
                          </Button>
                        </a>
                      )}
                      {market.phone && (
                        <a 
                          href={`tel:${market.phone}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button variant="outline" className="rounded-full" size="sm">
                            <Phone className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Registered Seller Stores Section */}
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-serif font-bold flex items-center gap-2">
                <ShoppingCart className="w-6 h-6 text-emerald-600" />
                {t('supermarket.shopOnline')}
              </h2>
              <p className="text-muted-foreground mt-1">
                {t('supermarket.shopOnlineDesc')}
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              {/* Location Filters for Stores */}
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger className="w-[160px] h-11 rounded-full" data-testid="store-country-filter">
                  <Globe className="w-4 h-4 mr-2" />
                  <SelectValue placeholder={t('location.selectCountry')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('location.allCountries')}</SelectItem>
                  {Object.keys(worldCountries).map((country) => (
                    <SelectItem key={country} value={country}>
                      {getCountryName(country)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select 
                value={selectedCity} 
                onValueChange={setSelectedCity}
                disabled={!selectedCountry || selectedCountry === 'all'}
              >
                <SelectTrigger className="w-[140px] h-11 rounded-full" data-testid="store-city-filter">
                  <SelectValue placeholder={t('location.selectCity')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('location.allCities')}</SelectItem>
                  {availableCities.map((city) => (
                    <SelectItem key={city} value={city}>{getCityName(city)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Badge variant="outline" className="h-11 px-4 text-sm">
                {stores.length} {t('supermarket.storesLabel')}
              </Badge>
            </div>
          </div>

          {loadingStores ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-16 w-16 rounded-xl mb-4" />
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-4" />
                    <Skeleton className="h-10 w-full rounded-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : stores.length === 0 ? (
            <Card className="p-8 bg-gradient-to-br from-emerald-50 to-white">
              <div className="text-center">
                <Store className="w-16 h-16 mx-auto text-emerald-300 mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  {t('supermarket.noOnlineStores')}
                </h3>
                <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                  {t('supermarket.noOnlineStoresDesc')}
                </p>
                <Link to="/become-seller">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-full">
                    {t('supermarket.becomeSeller')}
                  </Button>
                </Link>
              </div>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stores.map((store) => (
                <Card 
                  key={store.seller_id} 
                  className="group hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
                  onClick={() => navigate(`/supermarkets/store/${store.seller_id}`)}
                  data-testid={`store-${store.seller_id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                        <Store className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg group-hover:text-emerald-600 transition-colors truncate">
                          {store.seller_name}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="w-4 h-4" />
                          <span className="truncate">
                            {[store.district, store.city, store.country].filter(Boolean).join(', ') || t('common.locationNotSpecified')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {store.description && (
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {store.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge variant="secondary" className="text-xs">
                        <Package className="w-3 h-3 mr-1" />
                        {store.product_count} {t('supermarket.products')}
                      </Badge>
                      {store.delivery_available && (
                        <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200">
                          <Truck className="w-3 h-3 mr-1" />
                          {t('restaurant.delivery')}
                        </Badge>
                      )}
                      {store.rating > 0 && (
                        <Badge variant="outline" className="text-xs">
                          <Star className="w-3 h-3 mr-1 fill-yellow-400 text-yellow-400" />
                          {store.rating.toFixed(1)}
                        </Badge>
                      )}
                    </div>

                    {store.minimum_order > 0 && (
                      <p className="text-xs text-muted-foreground mb-3">
                        {t('supermarket.minOrder')}: ₺{store.minimum_order}
                      </p>
                    )}

                    <Button className="w-full rounded-full group-hover:bg-emerald-600" size="sm">
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      {t('supermarket.visitStore')}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Become a Seller CTA */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <Store className="w-16 h-16 mx-auto mb-6 opacity-80" />
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">
            {t('supermarket.sellerCta')}
          </h2>
          <p className="text-emerald-100 mb-8 max-w-2xl mx-auto text-lg">
            {t('supermarket.sellerCtaDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/become-seller">
              <Button size="lg" variant="secondary" className="rounded-full text-emerald-700 px-8">
                {t('supermarket.getStarted')}
              </Button>
            </Link>
            <Link to="/membership">
              <Button size="lg" variant="outline" className="rounded-full text-white border-white hover:bg-white/10 px-8">
                {t('supermarket.membershipPlans')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupermarketsPage;
